import { lazy, Suspense } from 'react';
import { cx } from '@/lib/cx';
import { t } from '@/lib/i18n';
import { Skeleton } from '../Skeleton';
import s from './Chart.module.css';

export interface ChartSeries {
  name: string;
  /** [time, value] — time as Date, ISO string or epoch ms; null = no data (a gap, not 0). */
  points: [Date | string | number, number | null][];
  kind?: 'line' | 'bar' | 'area';
  /** Categorical slot 1–8 (fixed order, never cycled; style reference §8.4). */
  slot?: number;
}

/** A vertical line at an instant (an event on a speed trace). */
export interface ChartMarker {
  at: Date | string | number;
  label?: string;
  tone?: 'primary' | 'good' | 'medium' | 'bad';
  /** Drawn solid and thicker (the selected one). */
  selected?: boolean;
}

export interface ChartProps {
  /** Accessible summary of what the chart shows. */
  label: string;
  series: ChartSeries[];
  markers?: ChartMarker[];
  unit?: string;
  height?: 'spark' | 'sm' | 'md' | 'lg';
  stacked?: boolean;
  yMin?: number;
  yMax?: number;
  /** The x axis' extent (e.g. the selected period), instead of the data's first and last point. */
  xMin?: Date | string | number;
  xMax?: Date | string | number;
  /**
   * What one point stands for. 'day': the axis shows dates and never ticks inside a day (a
   * per-day series with one or two days would otherwise be labelled in hours).
   */
  bucket?: 'day';
  className?: string;
}

const ChartInner = lazy(() => import('./ChartInner'));

/**
 * The only chart (RULES §UI-2): Apache ECharts, loaded on demand, themed from the tokens.
 * The x axis is always a real time scale in the company timezone.
 */
export function Chart({ height = 'md', className, ...props }: ChartProps) {
  return (
    <div className={cx(s.frame, s[height], className)} role="img" aria-label={props.label}>
      <Suspense fallback={<Skeleton height="100%" className={s.loading} />}>
        <ChartInner {...props} spark={height === 'spark'} />
      </Suspense>
      <span className="sr-only">{t('Chart')}</span>
    </div>
  );
}
